import React, { useState } from "react";
import "./styles/App.css";
import { PageLayout } from "./components/PageLayout";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from "@azure/msal-react";
import Button from "react-bootstrap/Button";
import { loginRequest } from "./authConfig";
import { callMsGraph } from "./graph";
import { ProfileData } from "./components/ProfileData";
import {
  Ec2ApiError,
  fetchManagedPrefixListEntries,
  modifyManagedPrefixList,
  getMyPublicIp,
} from "./api/ec2-api";
import {
  unlockUser,
  resetUserPassword,
  changeUserPassword,
} from "./api/ad-api";
import PasswordResetPopup from "./components/PasswordResetPopup";
import PasswordChangePopup from "./components/PasswordChangePopup";
import UserUnlockPopup from "./components/UserUnlockPopup";
import Toastify from "toastify-js";

const USER_DOMAIN = "idealsw.com";

/**
 * Renders information about the signed-in user or a button to retrieve data about the user
 */
const ProfileContent = () => {
  const { instance, accounts } = useMsal();
  const [graphData, setGraphData] = useState(null);
  const [prefixedListEntries, setPrefixedListEntries] = useState(null);
  const [myPublicIp, setMyPublicIp] = useState(null);
  const [myDescription, setMyDescription] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [prefixListError, setPrefixListError] = useState(null);
  const [showPasswordResetPopup, setShowPasswordResetPopup] = useState(false);
  const [showPasswordChangePopup, setShowPasswordChangePopup] = useState(false);
  const [showUserUnlockPopup, setShowUserUnlockPopup] = useState(false);

  const openPasswordResetPopup = () => {
    setShowPasswordResetPopup(true);
  };

  const closePasswordResetPopup = () => {
    setShowPasswordResetPopup(false);
  };

  const openPasswordChangePopup = () => {
    setShowPasswordChangePopup(true);
  };

  const closePasswordChangePopup = () => {
    setShowPasswordChangePopup(false);
  };

  const openUserUnlockPopup = () => {
    setShowUserUnlockPopup(true);
  };

  const closeUserUnlockPopup = () => {
    setShowUserUnlockPopup(false);
  };

  async function RequestProfileData() {
    // Silently acquires an access token which is then attached to a request for MS Graph data
    instance
      .acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      })
      .then((response) => {
        callMsGraph(response.accessToken).then((response) =>
          setGraphData(response)
        );
      });
  }

  /*   {
    "prefixListId": "pl-0209175611caa97cf",
    "currentVersion": "16",
    "prefixListEntries": [
        {
            "cidr": "94.71.99.53/32",
            "description": "stelios home"
        }
    ]
} */

  async function UnlockMyUser() {
    setIsLoading(true);

    //split username@USER_DOMAIN
    let username = graphData?.userPrincipalName?.split("@")[0];
    const domain = graphData?.userPrincipalName?.split("@")[1];
    //add the dot back to username for i-docs.com
    username = username.charAt(0) + "." + username.substring(1);

    if (domain !== USER_DOMAIN) {
      setIsLoading(false);

      Toastify({
        text: `Unlocking user accounts is allowed only for ${USER_DOMAIN} users`,
        //className: "info",
        newWindow: true,
        gravity: "top", // `top` or `bottom`
        position: "center", // `left`, `center` or `right`
        style: {
          background: "linear-gradient(to left, #b0009b, #c9003d)",
          color: "white",
        },
      }).showToast();

      return null;
    }

    const response = await unlockUser(username);

    response === true
      ? Toastify({
          text: "You have successfully unlocked your user",
          //className: "info",
          newWindow: true,
          gravity: "top", // `top` or `bottom`
          position: "center", // `left`, `center` or `right`
          style: {
            background: "linear-gradient(to left, #00b09b, #96c93d)",
          },
        }).showToast()
      : Toastify({
          text: "Failure unlocking your user",
          //className: "info",
          newWindow: true,
          gravity: "top", // `top` or `bottom`
          position: "center", // `left`, `center` or `right`
          style: {
            background: "linear-gradient(to left, #b0009b, #c9003d)",
            color: "white",
          },
        }).showToast();

    setIsLoading(false);

    return response;
  }

  async function ResetMyUserPassword(newPassword) {
    setIsLoading(true);

    //split username@USER_DOMAIN
    let username = graphData?.userPrincipalName?.split("@")[0];
    const domain = graphData?.userPrincipalName?.split("@")[1];
    //add the dot back to username for i-docs.com
    username = username.charAt(0) + "." + username.substring(1);

    if (domain !== USER_DOMAIN) {
      setIsLoading(false);

      Toastify({
        text: `Resetting user passwords is allowed only for ${USER_DOMAIN} users`,
        //className: "info",
        newWindow: true,
        gravity: "top", // `top` or `bottom`
        position: "center", // `left`, `center` or `right`
        style: {
          background: "linear-gradient(to left, #b0009b, #c9003d)",
          color: "white",
        },
      }).showToast();

      return null;
    }

    //const newPassword = "grrd24!@#";
    //const newUsername = "g.rodopoulos";

    const response = await resetUserPassword(username, newPassword);

    response === true
      ? Toastify({
          text: "You have successfully reset your user password",
          //className: "info",
          newWindow: true,
          gravity: "top", // `top` or `bottom`
          position: "center", // `left`, `center` or `right`
          style: {
            background: "linear-gradient(to left, #00b09b, #96c93d)",
          },
        }).showToast()
      : Toastify({
          text: "Failure resetting your user password",
          //className: "info",
          newWindow: true,
          gravity: "top", // `top` or `bottom`
          position: "center", // `left`, `center` or `right`
          style: {
            background: "linear-gradient(to left, #b0009b, #c9003d)",
            color: "white",
          },
        }).showToast();

    setIsLoading(false);

    return response;
  }

  async function ChangeMyUserPassword(currentPassword, newPassword) {
    setIsLoading(true);

    //split username@USER_DOMAIN
    let username = graphData?.userPrincipalName?.split("@")[0];
    const domain = graphData?.userPrincipalName?.split("@")[1];
    //add the dot back to username for i-docs.com
    username = username.charAt(0) + "." + username.substring(1);

    if (domain !== USER_DOMAIN) {
      setIsLoading(false);

      Toastify({
        text: `Changing user passwords is allowed only for ${USER_DOMAIN} users`,
        //className: "info",
        newWindow: true,
        gravity: "top", // `top` or `bottom`
        position: "center", // `left`, `center` or `right`
        style: {
          background: "linear-gradient(to left, #b0009b, #c9003d)",
          color: "white",
        },
      }).showToast();

      return null;
    }

    //const currentPassword = "grrd24!@#";
    //const newPassword = "grrd24!@#";
    //const newUsername = "g.rodopoulos";

    const response = await changeUserPassword(
      username,
      currentPassword,
      newPassword
    );

    response === true
      ? Toastify({
          text: "You have successfully changed your user password",
          //className: "info",
          newWindow: true,
          gravity: "top", // `top` or `bottom`
          position: "center", // `left`, `center` or `right`
          style: {
            background: "linear-gradient(to left, #00b09b, #96c93d)",
          },
        }).showToast()
      : Toastify({
          text: "Failure changing your user password",
          //className: "info",
          newWindow: true,
          gravity: "top", // `top` or `bottom`
          position: "center", // `left`, `center` or `right`
          style: {
            background: "linear-gradient(to left, #b0009b, #c9003d)",
            color: "white",
          },
        }).showToast();

    setIsLoading(false);

    return response;
  }

  async function GetAwsManagedPrefixListEntries() {
    setIsLoading(true);

    //split username@USER_DOMAIN
    let username = graphData?.userPrincipalName?.split("@")[0];
    const domain = graphData?.userPrincipalName?.split("@")[1];
    //add the dot back to username for i-docs.com
    username = username.charAt(0) + "." + username.substring(1);

    if (domain !== USER_DOMAIN) {
      setIsLoading(false);

      Toastify({
        text: `Getting Public IP addresses for accessing AWS resources is allowed only for ${USER_DOMAIN} users`,
        //className: "info",
        newWindow: true,
        gravity: "top", // `top` or `bottom`
        position: "center", // `left`, `center` or `right`
        style: {
          background: "linear-gradient(to left, #b0009b, #c9003d)",
          color: "white",
        },
      }).showToast();

      return null;
    }

    //remove dot from d.pantazi because is the only user in i-docs.com that its prefix list does not comply with the rules
    let prefixListName = username;
    if (username === "d.pantazi") {
      prefixListName = "dpantazi";
    }

    try {
    const data = await fetchManagedPrefixListEntries(instance, accounts[0], prefixListName);

    const myNewPublicIp = await getMyPublicIp();

    setPrefixedListEntries(data);
    setPrefixListError(null);
    setMyPublicIp(myNewPublicIp);
    setMyDescription("");
    setIsLoading(false);
    //console.log(data);

    return data;
  } catch (error) {
    setIsLoading(false);
    if (error instanceof Ec2ApiError) {
      console.error("Prefix list fetch failed:", error.kind, error.message, error.cause);
      setPrefixListError(error);
      // Don't wipe a previously loaded list on a transient auth/network error
      // so the user can retry without losing context.
      return null;
    }
    console.error("Unexpected prefix list fetch error:", error);
    setPrefixListError(
      new Ec2ApiError("network", "An unexpected error occurred. Please try again.")
    );
    return null;
  }
}

  function handleDescriptionChange(e) {
    //console.log(myDescription[e.target.name]);
    setMyDescription(e.target.value);
  }

  async function SetAwsManagedPrefixListEntries(e) {
    const prefixListId = prefixedListEntries.prefixListId;
    const currentVersion = prefixedListEntries.currentVersion;
    const addEntryCidr = myPublicIp;
    const addEntryDescription = encodeURI(myDescription);
    const removeEntryCidr = e.target.id;

    setIsLoading(true);

    /*     console.log(prefixListId);
    console.log(currentVersion);
    console.log(addEntryCidr);
    console.log(addEntryDescription);
    console.log(removeEntryCidr); */
    try {
      await modifyManagedPrefixList(
        instance,
        accounts[0],
        prefixListId,
        currentVersion,
        removeEntryCidr,
        addEntryCidr,
        addEntryDescription
      );
    } catch (error) {
      await new Promise((r) => setTimeout(r, 3000));

      const message =
        error instanceof Ec2ApiError
          ? error.kind === "auth"
            ? "Your session has expired. Please sign in again."
            : error.kind === "network"
            ? "Could not reach the EC2 API service."
            : `Failed to change your public IP address: ${error.message}`
          : "Failed to change your public IP address";

      Toastify({
        text: message,
        newWindow: true,
        gravity: "top", // `top` or `bottom`
        position: "center", // `left`, `center` or `right`
        style: {
          background: "linear-gradient(to left, #b0009b, #c9003d)",
          color: "white",
        },
      }).showToast();

      setIsLoading(false);
      return null;
    }

    await new Promise((r) => setTimeout(r, 3000));

    const data = await GetAwsManagedPrefixListEntries();

    setPrefixedListEntries(data);
    setIsLoading(false);

    Toastify({
      text: "You have successfully changed your public IP address",
      //className: "info",
      newWindow: true,
      gravity: "top", // `top` or `bottom`
      position: "center", // `left`, `center` or `right`
      style: {
        background: "linear-gradient(to left, #00b09b, #96c93d)",
      },
    }).showToast();

    return data;
  }

  return (
    <div className="flex-container">
      <h5 className="card-title">Welcome {accounts[0].name}</h5>
      {graphData ? (
        <ProfileData graphData={graphData} />
      ) : (
        <Button variant="secondary" onClick={RequestProfileData}>
          Validate my Microsoft 365 Profile Information
        </Button>
      )}
      <Button
        disabled={isLoading || !graphData}
        variant="secondary"
        onClick={openUserUnlockPopup}
      >
        Unlock my i-DOCS User
      </Button>
      <UserUnlockPopup
        show={showUserUnlockPopup}
        onClose={closeUserUnlockPopup}
        onUnlockUser={UnlockMyUser}
      />

      <Button
        disabled={isLoading || !graphData}
        variant="secondary"
        onClick={openPasswordResetPopup}
      >
        Reset my i-DOCS User Password
      </Button>
      <PasswordResetPopup
        show={showPasswordResetPopup}
        onClose={closePasswordResetPopup}
        onResetPassword={ResetMyUserPassword}
      />

      <Button
        disabled={isLoading || !graphData}
        variant="secondary"
        onClick={openPasswordChangePopup}
      >
        Change my i-DOCS User Password
      </Button>
      <PasswordChangePopup
        show={showPasswordChangePopup}
        onClose={closePasswordChangePopup}
        onChangePassword={ChangeMyUserPassword}
      />

      <Button
        disabled={isLoading || !graphData}
        variant="secondary"
        onClick={GetAwsManagedPrefixListEntries}
      >
        Manage my Amazon Public IPs
      </Button>
      {prefixedListEntries ? (
        <div>
          <div className="flex-row">
            <span className="header">Public IP Address</span>
            <span className="header">Description</span>
            <span className="header">Action</span>
          </div>
          {prefixedListEntries.prefixListEntries.map((entry) => (
            <div key={entry.cidr} className="flex-row">
              <span className="span">{entry.cidr}</span>
              <span className="span">
                <input
                  onChange={(e) => handleDescriptionChange(e)}
                  type="text"
                  id={entry.cidr}
                  name={entry.cidr}
                  //value={myDescription}
                  placeholder={entry.description}
                />
              </span>
              <span className="span">
                {entry.cidr !== myPublicIp ? (
                  <Button
                    disabled={isLoading}
                    id={entry.cidr}
                    variant="primary"
                    onClick={SetAwsManagedPrefixListEntries}
                  >
                    Replace with {myPublicIp}
                  </Button>
                ) : (
                  <span>Public IP address is up-to-date.</span>
                )}
              </span>
            </div>
          ))}
        </div>
      ) : prefixListError ? (
        <div>
          {prefixListError.kind === "not_found" ? (
            <div>
              There are no Public IPs for AWS access registered for your username.
            </div>
          ) : (
            <div>
              <div>
                {prefixListError.kind === "auth"
                  ? "Your session may have expired. Please sign in again and retry."
                  : prefixListError.kind === "network"
                  ? "Could not reach the EC2 API service. It may be down or unreachable. Try again in a moment."
                  : `The EC2 API service returned an error: ${prefixListError.message}`}
              </div>
              <Button
                disabled={isLoading}
                variant="secondary"
                onClick={GetAwsManagedPrefixListEntries}
              >
                Retry
              </Button>
            </div>
          )}
        </div>
      ) : myPublicIp ? (
        <div>
          There are no Public IPs for AWS access registered for your username.
        </div>
      ) : (
        <div />
      )}
    </div>
  );
};

/**
 * If a user is authenticated the ProfileContent component above is rendered. Otherwise a message indicating a user is not authenticated is rendered.
 */
const MainContent = () => {
  return (
    <div className="App">
      <AuthenticatedTemplate>
        <ProfileContent />
      </AuthenticatedTemplate>

      <UnauthenticatedTemplate>
        <h5 className="card-title">
          Please sign-in to see your available actions.
        </h5>
      </UnauthenticatedTemplate>
    </div>
  );
};

export default function App() {
  return (
    <PageLayout>
      <MainContent />
    </PageLayout>
  );
}
